/**
 * EvaluateBrowser — per-company teardown view.
 *
 * Layman-first presentation: a plain-English "Quick read" up top, friendly
 * section names (the A/B/C/D lens codes are kept only as quiet subtitles),
 * gap scores rendered as meaningful badges, and de-jargoned copy throughout.
 * The underlying rigor (sources, exact scores, honest deferrals) is preserved
 * one glance away — nothing is fabricated.
 */
import React, { useEffect, useMemo, useState } from "react";
import axios from "axios";
import RevenueProofCard from "../components/RevenueProofCard";
import GuideHelpButton from "../components/GuideHelpButton";

const API = `${process.env.REACT_APP_BACKEND_URL}/api`;

const COUNTRY_NAMES = {
  AU: "Australia", CA: "Canada", DE: "Germany", DK: "Denmark", EE: "Estonia",
  ES: "Spain", FR: "France", GB: "United Kingdom", IE: "Ireland", IL: "Israel",
  IN: "India", NL: "Netherlands", NZ: "New Zealand", SG: "Singapore", US: "United States",
  AE: "United Arab Emirates", SA: "Saudi Arabia", JP: "Japan", KR: "South Korea",
  IT: "Italy", AT: "Austria", BE: "Belgium", BR: "Brazil", CH: "Switzerland",
  CZ: "Czech Republic", FI: "Finland", MX: "Mexico", NO: "Norway", PL: "Poland",
  PT: "Portugal", SE: "Sweden",
};

const INDUSTRY_LABELS = {
  ai_ml: "AI/ML", climate: "Climate", consumer: "Consumer",
  ecommerce_marketplace: "E-commerce/Marketplace", education: "Education",
  fintech: "Fintech", healthcare_bio: "Healthcare/Bio",
  industrials_dynamism: "Industrials", legal_tech: "Legal Tech",
  productivity: "Productivity", real_estate_proptech: "Real Estate/PropTech",
  saas_b2b: "SaaS B2B",
};

const PAIN_BUCKET_LABEL = {
  speed: "⚡ too slow", cost: "💸 too expensive", integration: "🔌 hard to integrate",
  hallucination: "🤖 unreliable AI", quality: "✦ poor quality", privacy: "🔒 privacy worries",
  domain_niche: "🎯 not a fit", video: "🎬 video issues",
};
const PAIN_BUCKET_PLAIN = {
  speed: "speed", cost: "price", integration: "integrations", hallucination: "unreliable AI",
  quality: "quality", privacy: "privacy", domain_niche: "fit for their niche", video: "video",
};

// Plain name for each evidence lens — we keep A/B/C/D only as a quiet subtitle.
const LENS_PLAIN = { A: "where to launch it", B: "missing features", C: "customer complaints", D: "pricing gaps" };
const CONFIDENCE_PLAIN = { strong: "high confidence", moderate: "medium confidence", weak: "early signal" };

const GAP_SCALE_HINT =
  "Opportunity score: under 2 = small · 2–3 = worth a look · 3–5 = strong · 5+ = rare/elite. Higher means fewer competitors are already doing this.";

// Turn a raw gap score into a plain-English verdict + colors.
function gapMeaning(score) {
  const s = Number(score) || 0;
  if (s >= 5) return { word: "Rare opportunity", dot: "bg-emerald-500", text: "text-emerald-700", bg: "bg-emerald-50 border-emerald-200" };
  if (s >= 3) return { word: "Strong opportunity", dot: "bg-emerald-500", text: "text-emerald-700", bg: "bg-emerald-50 border-emerald-200" };
  if (s >= 2) return { word: "Worth a look", dot: "bg-amber-500", text: "text-amber-700", bg: "bg-amber-50 border-amber-200" };
  return { word: "Small signal", dot: "bg-zinc-400", text: "text-zinc-500", bg: "bg-zinc-50 border-zinc-200" };
}

function GapBadge({ score }) {
  const m = gapMeaning(score);
  return (
    <span title={GAP_SCALE_HINT}
      className={`inline-flex items-center gap-1.5 rounded-full border px-2.5 py-1 ${m.bg}`}
      data-testid="gap-badge">
      <span className={`w-1.5 h-1.5 rounded-full ${m.dot}`} />
      <span className={`text-xs font-semibold ${m.text}`}>{m.word}</span>
      <span className="text-[10px] text-zinc-400 font-mono">{Number(score).toFixed(1)}</span>
    </span>
  );
}

// ── "Should I build this?" — an instant, honest go/no-go derived from the same
// grounded signals (never fabricated). Green when there's a real empty market
// PLUS a clear way to win (customer pain or a pricing lane); amber otherwise.
function BuildVerdict({ data, name, id }) {
  const [copied, setCopied] = useState(false);
  const a = data.lens_a_geographic;
  const c = data.lens_c_pain;
  const d = data.lens_d_market;
  const b = data.lens_b_features;

  const topGap = a?.available ? (a.top_gaps?.[0]?.gap_score ?? 0) : 0;
  const strongGap = topGap >= 3;
  const hasPain = !!(c?.available && c.quotes?.length);
  const hasLane = !!(d?.available && ((d.segments_uncovered?.length || 0) + (d.pricing_gaps?.length || 0) > 0));
  const hasMissing = !!(b?.available && !b.focal_unverified && b.missing_features?.length);

  const anyData = a?.available || c?.available || d?.available || b?.available;
  if (!anyData) return null;

  const go = strongGap && (hasPain || hasLane);

  const verdict = go
    ? { emoji: "✅", word: "Worth building", line: "There's a genuinely empty market here — plus a clear way to win customers.", bg: "bg-emerald-50 border-emerald-200", chip: "text-emerald-700", bar: "bg-emerald-500" }
    : { emoji: "⚠️", word: "Proceed with caution", line: "There's some signal, but the case isn't fully stacked yet — verify the gaps below before you commit.", bg: "bg-amber-50 border-amber-200", chip: "text-amber-700", bar: "bg-amber-500" };

  const checks = [
    { ok: strongGap, label: strongGap ? "Strong market gap" : "No standout market gap" },
    { ok: hasPain, label: hasPain ? "Real customer pain" : "No customer-pain data" },
    { ok: hasLane, label: hasLane ? "Open pricing lane" : "No clear pricing lane" },
    { ok: hasMissing, label: hasMissing ? "Features to add" : "No missing features found" },
  ];

  // Shareable one-liner — e.g. "✅ Worth building: clone Airbnb for Singapore"
  const topCountry = strongGap && a?.top_gaps?.[0] ? (COUNTRY_NAMES[a.top_gaps[0].missing_country] || a.top_gaps[0].missing_country) : null;
  const concept = topCountry ? `clone ${name} for ${topCountry}` : (name ? `evaluate ${name}` : "this opportunity");
  const onShare = async () => {
    const origin = typeof window !== "undefined" ? window.location.origin : "";
    // Human-facing app link (deep-links into the teardown):
    const appUrl = id ? `${origin}/evaluate/${id}` : (typeof window !== "undefined" ? window.location.href : "");
    // Shareable link goes through the backend so chat apps unfurl a rich OG card,
    // then redirects the human to appUrl.
    const shareUrl = id ? `${origin}/api/share/evaluate/${id}` : appUrl;
    const text = `${verdict.emoji} ${verdict.word}: ${concept} — found on Prime Pillar`;
    if (typeof navigator !== "undefined" && navigator.share) {
      try { await navigator.share({ title: "Prime Pillar", text, url: shareUrl }); return; } catch { /* user cancelled — fall through */ }
    }
    try {
      await navigator.clipboard.writeText(`${text}\n${shareUrl}`);
      setCopied(true);
      setTimeout(() => setCopied(false), 2200);
    } catch { /* clipboard blocked — no-op */ }
  };

  return (
    <div className={`rounded-xl border ${verdict.bg} overflow-hidden`} data-testid="build-verdict">
      <div className={`h-1 w-full ${verdict.bar}`} />
      <div className="p-4 sm:p-5">
        <div className="flex items-start justify-between gap-3">
          <div className="flex items-start gap-3 min-w-0">
            <span className="text-2xl leading-none mt-0.5">{verdict.emoji}</span>
            <div className="min-w-0">
              <div className="text-[11px] font-semibold uppercase tracking-wider text-zinc-400">Should I build this?</div>
              <div className="text-lg font-bold text-zinc-900 leading-tight" data-testid="build-verdict-word">{verdict.word}</div>
              <p className="text-[14px] text-zinc-700 leading-relaxed mt-0.5">{verdict.line}</p>
            </div>
          </div>
          <button onClick={onShare} data-testid="build-verdict-share"
            className="flex-shrink-0 inline-flex items-center gap-1.5 text-[12px] font-semibold px-3 py-1.5 rounded-full bg-white border border-zinc-200 text-zinc-700 hover:border-violet-300 hover:text-violet-700 transition-colors"
            title="Share this verdict">
            {copied ? <>✓ Copied</> : <>↗ Share</>}
          </button>
        </div>
        <div className="flex flex-wrap gap-2 mt-3.5">
          {checks.map((ck, i) => (
            <span key={i} className={`inline-flex items-center gap-1.5 text-[12px] px-2.5 py-1 rounded-full border bg-white ${ck.ok ? "border-emerald-200 text-emerald-700" : "border-zinc-200 text-zinc-400"}`} data-testid={`verdict-check-${i}`}>
              <span>{ck.ok ? "✓" : "✗"}</span>{ck.label}
            </span>
          ))}
        </div>
        <p className="text-[11px] text-zinc-400 mt-3">A quick read of the evidence below — not financial advice. Always verify before acting.</p>
      </div>
    </div>
  );
}

// ── "Quick read" — the whole teardown in 2-4 plain bullets, derived client-side
// from the same grounded data (no new claims). This is what a layman reads first.
function QuickRead({ data, name }) {
  const bullets = [];

  const a = data.lens_a_geographic;
  if (a?.available && a.top_gaps?.length) {
    const top = a.top_gaps[0];
    const country = COUNTRY_NAMES[top.missing_country] || top.missing_country;
    const more = a.top_gaps.length - 1;
    bullets.push({
      icon: "🌍",
      text: <>Best new market: <b>{country}</b> has no local version of this yet{more > 0 ? <> (plus {more} other {more === 1 ? "market" : "markets"})</> : null}.</>,
    });
  }

  const c = data.lens_c_pain;
  if (c?.available && c.quotes?.length) {
    const counts = {};
    c.quotes.forEach((q) => { if (q.pain_bucket) counts[q.pain_bucket] = (counts[q.pain_bucket] || 0) + 1; });
    const topBuckets = Object.entries(counts).sort((x, y) => y[1] - x[1]).slice(0, 2).map(([k]) => PAIN_BUCKET_PLAIN[k] || k);
    bullets.push({
      icon: "😤",
      text: topBuckets.length
        ? <>Customers of today's tools complain most about <b>{topBuckets.join(" and ")}</b> — beat that and you win them over.</>
        : <>Customers of today's tools have real, unanswered complaints you could win on.</>,
    });
  }

  const b = data.lens_b_features;
  if (b?.available) {
    const missing = b.missing_features || [];
    if (!b.focal_unverified && missing.length) {
      bullets.push({ icon: "🧩", text: <><b>{missing.length}</b> feature{missing.length === 1 ? "" : "s"} that similar companies offer but {name} may be missing.</> });
    }
  }

  const d = data.lens_d_market;
  if (d?.available) {
    if (d.segments_uncovered?.length) {
      bullets.push({ icon: "💰", text: <>No competitor serves the <b>{d.segments_uncovered.join(", ")}</b> price tier — an open lane to undercut or specialise.</> });
    } else if (d.cheapest_entry) {
      bullets.push({ icon: "💰", text: <>Cheapest competitor charges <b>{d.cheapest_entry.currency || "$"}{d.cheapest_entry.monthly_equiv}/mo</b> — your room to price under it.</> });
    }
  }

  if (!bullets.length) return null;

  return (
    <div className="rounded-xl border border-zinc-200 bg-white p-4 sm:p-5" data-testid="quick-read">
      <div className="text-[11px] font-semibold uppercase tracking-wider text-zinc-400 mb-3">Quick read — in plain English</div>
      <ul className="space-y-2.5">
        {bullets.map((b, i) => (
          <li key={i} className="flex items-start gap-2.5 text-[14px] text-zinc-800 leading-relaxed">
            <span className="text-base leading-none mt-0.5">{b.icon}</span>
            <span>{b.text}</span>
          </li>
        ))}
      </ul>
      <p className="text-[11px] text-zinc-400 mt-3 pt-3 border-t border-zinc-100">
        Every claim below links to its real source. Where we don't have data, we say so instead of guessing.
      </p>
    </div>
  );
}

function LensCard({ icon, title, subtitle, code, available, children, deferral, testid }) {
  return (
    <div className="rounded-xl border border-zinc-200 bg-white overflow-hidden" data-testid={testid}>
      <div className={`px-4 py-3 border-b ${available ? "bg-zinc-50 border-zinc-200" : "bg-amber-50 border-amber-200"}`}>
        <div className="flex items-center justify-between gap-2">
          <div className="flex items-center gap-2.5 min-w-0">
            <span className="text-lg leading-none flex-shrink-0">{icon}</span>
            <div className="min-w-0">
              <div className="text-sm font-bold text-zinc-900 leading-tight">{title}</div>
              <div className="text-[11px] text-zinc-500 leading-tight">{subtitle}</div>
            </div>
          </div>
          {available
            ? <span className="text-[10px] font-mono uppercase tracking-wider text-zinc-300 flex-shrink-0">Lens {code}</span>
            : <span className="text-[10px] font-medium text-amber-700 bg-amber-100 px-2 py-0.5 rounded-full flex-shrink-0">No data yet</span>}
        </div>
      </div>
      <div className="p-4">
        {!available && deferral && (
          <p className="text-[13px] text-amber-800 leading-relaxed">
            We don't have enough verified data here, so we won't guess. <span className="text-amber-700/80">{deferral}</span>
          </p>
        )}
        {available && children}
      </div>
    </div>
  );
}

function LensA({ data }) {
  if (!data?.available) return null;
  return (
    <div className="space-y-2" data-testid="lens-a-gaps">
      <p className="text-[13px] text-zinc-600 mb-1">
        Markets where this company's idea has <b>no local equivalent yet</b> — the stronger the badge, the emptier the market:
      </p>
      {data.top_gaps.slice(0, 6).map((g, i) => (
        <div key={i} className="flex items-center justify-between gap-2 rounded-lg border border-zinc-200 px-3 py-2.5">
          <div className="min-w-0">
            <span className="font-semibold text-zinc-900">{COUNTRY_NAMES[g.missing_country] || g.missing_country}</span>
            <span className="ml-2 text-[11px] text-zinc-400">{CONFIDENCE_PLAIN[g.corroboration_quality] || g.corroboration_quality}</span>
          </div>
          <GapBadge score={g.gap_score} />
        </div>
      ))}
    </div>
  );
}

function LensC({ data }) {
  if (!data?.available) return null;
  return (
    <div className="space-y-3" data-testid="lens-c-quotes">
      <p className="text-[13px] text-zinc-600">
        Real 1-star reviews of the established tools people use today. These are the problems any newcomer would need to solve.
      </p>
      {data.incumbents_sampled?.length > 0 && (
        <div className="text-[12px] text-zinc-500">
          Complaints we looked at: <span className="font-medium text-zinc-700">{data.incumbents_sampled.join(", ")}</span>
        </div>
      )}
      {data.quotes.map((q, i) => (
        <blockquote key={i} className="border-l-4 border-amber-300 bg-amber-50/40 pl-3 pr-2 py-2 rounded-r" data-testid={`pain-quote-${i}`}>
          <div className="flex items-center gap-2 mb-1 flex-wrap">
            <span className="text-[12px] font-semibold text-amber-900">{q.company}</span>
            {q.pain_bucket && PAIN_BUCKET_LABEL[q.pain_bucket] && (
              <span className="text-[10px] px-1.5 py-0.5 rounded-full bg-amber-200 text-amber-900">{PAIN_BUCKET_LABEL[q.pain_bucket]}</span>
            )}
            <span className="text-[11px] text-zinc-400">★ 1-star review</span>
          </div>
          {q.title && q.title !== `[${q.company}] ${q.title.replace(/^\[[^\]]+\]\s*/, "")}` && (
            <div className="text-[13px] font-semibold text-zinc-800 mb-1">{q.title.replace(/^\[[^\]]+\]\s*/, "")}</div>
          )}
          <p className="text-[13px] text-zinc-700 leading-relaxed line-clamp-5">{q.body}</p>
          {q.source_url && (
            <a href={q.source_url} target="_blank" rel="noopener noreferrer"
              className="text-[11px] font-medium text-violet-700 hover:underline mt-1 inline-block">
              Read the review ↗
            </a>
          )}
        </blockquote>
      ))}
    </div>
  );
}

function LensD({ data }) {
  if (!data?.available) return null;
  const priced = data.incumbents_priced || [];
  const gaps = data.pricing_gaps || [];
  return (
    <div className="space-y-4" data-testid="lens-d-pricing">
      <p className="text-[13px] text-zinc-600">
        What today's tools charge — and the price points or customer tiers <b>no one is serving</b>.
      </p>

      {gaps.length > 0 && (
        <div className="space-y-2">
          <div className="text-[12px] font-semibold text-zinc-700">Open lanes in the market</div>
          {gaps.map((g, i) => (
            <div key={i} className="rounded-lg border border-amber-200 bg-amber-50/60 px-3 py-2 text-[13px] text-amber-900" data-testid={`pricing-gap-${i}`}>{g}</div>
          ))}
        </div>
      )}

      {data.cheapest_entry && (
        <div className="text-[13px] text-zinc-700">
          Cheapest competitor: <span className="font-semibold text-zinc-900">{data.cheapest_entry.currency || "$"}{data.cheapest_entry.monthly_equiv}/mo</span> ({data.cheapest_entry.incumbent})
          {(data.segments_covered?.length > 0) && <> · serves: {data.segments_covered.join(", ")}</>}
          {data.segments_uncovered?.length > 0 && <span className="text-amber-700"> · nobody serves: {data.segments_uncovered.join(", ")}</span>}
        </div>
      )}

      <div>
        <div className="text-[12px] font-semibold text-zinc-700 mb-2">What today's tools charge ({priced.length} with public pricing)</div>
        <div className="space-y-1.5">
          {priced.map((p, i) => (
            <div key={i} className="flex items-center justify-between gap-2 rounded-lg border border-zinc-200 px-3 py-2" data-testid={`incumbent-price-${i}`}>
              <div className="min-w-0">
                <span className="text-[13px] font-semibold text-zinc-900">{p.name}</span>
                {p.free_tier && <span className="ml-1.5 text-[10px] px-1.5 py-0.5 rounded-full bg-emerald-100 text-emerald-700">free tier</span>}
                <span className="ml-1.5 text-[11px] text-zinc-500">{(p.segments || []).join(", ")}</span>
              </div>
              <div className="flex items-center gap-2 flex-shrink-0">
                <span className="text-[13px] font-semibold text-violet-700">
                  {p.lowest_price?.amount === 0 ? "Free" : `${p.lowest_price?.currency || "$"}${p.lowest_price?.amount}/${(p.lowest_price?.period || "mo").slice(0, 2)}`}
                </span>
                {p.source_url && (
                  <a href={p.source_url} target="_blank" rel="noopener noreferrer" className="text-[11px] text-zinc-400 hover:text-violet-700" title={p.source_url}>source ↗</a>
                )}
              </div>
            </div>
          ))}
        </div>
      </div>

      {data.incumbents_no_pricing?.length > 0 && (
        <p className="text-[12px] text-zinc-500">
          Hidden / quote-only pricing (we won't guess these): {data.incumbents_no_pricing.join(", ")}.
        </p>
      )}
      {data.caveat && (
        <p className="text-[12px] text-zinc-500 italic leading-relaxed border-t border-zinc-100 pt-2 mt-2">{data.caveat}</p>
      )}
    </div>
  );
}

function LensB({ data, centroidName }) {
  if (!data?.available) return null;
  const missing = data.missing_features || [];
  const common = data.cluster_common_features || [];
  const focal = data.focal_features || [];
  const unverified = data.focal_unverified;
  const list = unverified ? common : missing;
  return (
    <div className="space-y-4" data-testid="lens-b-features">
      <p className="text-[13px] text-zinc-600">
        Capabilities that similar companies offer — useful as a checklist of what {centroidName} may be missing.
      </p>

      {unverified && (
        <div className="rounded-lg border border-amber-300 bg-amber-50 px-3 py-2" data-testid="lens-b-unverified-warning">
          <div className="text-[13px] text-amber-900 leading-relaxed">
            We couldn't read {centroidName}'s own feature list clearly, so the list below is <b>not confirmed gaps</b> — it's what {data.siblings_analyzed} similar companies have in common. Treat it as a checklist to verify against {centroidName}.
          </div>
        </div>
      )}

      <div>
        <div className="text-[12px] font-semibold text-zinc-700 mb-2">
          {unverified ? `Common features among ${data.siblings_analyzed} similar companies` : `Features ${centroidName} may be missing`}
        </div>
        {list.length === 0 ? (
          <p className="text-[13px] text-zinc-500 italic">
            Nothing stood out — {centroidName} seems to cover what its peers offer (or we're still analysing its peers).
          </p>
        ) : (
          <div className="space-y-2">
            {list.map((m, i) => (
              <div key={i} className={`rounded-lg border px-3 py-2 ${unverified ? "border-amber-200 bg-amber-50/40" : "border-rose-200 bg-rose-50/50"}`} data-testid={`missing-feature-${i}`}>
                <div className="flex items-center gap-2 mb-1 flex-wrap">
                  <span className={`text-[13px] font-semibold ${unverified ? "text-amber-800" : "text-rose-800"}`}>{m.feature}</span>
                  <span className={`text-[10px] px-1.5 py-0.5 rounded-full ${unverified ? "bg-amber-200 text-amber-900" : "bg-rose-200 text-rose-900"}`}>
                    {m.sibling_count} of {data.siblings_analyzed} similar companies offer this
                  </span>
                </div>
                <div className="space-y-0.5">
                  {m.siblings.map((s, j) => (
                    <div key={j} className="text-[12px] text-zinc-600">
                      <span className="font-semibold text-zinc-800">{s.name}</span>
                      {s.evidence && <span className="text-zinc-500"> — &ldquo;{s.evidence}&rdquo;</span>}
                    </div>
                  ))}
                </div>
              </div>
            ))}
          </div>
        )}
      </div>

      {focal.length > 0 && (
        <div>
          <div className="text-[12px] font-semibold text-zinc-700 mb-2">What {centroidName} already does</div>
          <div className="flex flex-wrap gap-1.5">
            {focal.map((f, i) => (
              <span key={i} className="text-[12px] px-2 py-1 rounded-full bg-emerald-50 text-emerald-800 border border-emerald-200" title={f.evidence} data-testid={`focal-feature-${i}`}>
                {f.feature}
              </span>
            ))}
          </div>
        </div>
      )}

      {data.caveat && (
        <p className="text-[12px] text-zinc-500 italic leading-relaxed border-t border-zinc-100 pt-2 mt-2">{data.caveat}</p>
      )}
    </div>
  );
}

function EvaluateDetailPanel({ id, onClose }) {
  const [data, setData] = useState(null);
  const [err, setErr] = useState(null);

  useEffect(() => {
    let cancel = false;
    setData(null); setErr(null);
    (async () => {
      try {
        const r = await axios.get(`${API}/evaluate/companies/${id}`);
        if (!cancel) setData(r.data);
      } catch (e) {
        if (!cancel) setErr(e.response?.data?.detail || e.message);
      }
    })();
    return () => { cancel = true; };
  }, [id]);

  if (err) return <div className="p-6 text-red-600 text-sm" data-testid="eval-detail-error">Failed: {err}</div>;
  if (!data) return <div className="p-6 text-zinc-500 text-sm" data-testid="eval-detail-loading">Loading teardown…</div>;
  const h = data.header;
  const syn = data.synthesis;
  const primaryPlain = syn?.primary_lens ? (LENS_PLAIN[syn.primary_lens] || syn.primary_lens) : null;
  const backedBy = (syn?.evidence_lenses || []).map((l) => LENS_PLAIN[l] || l);

  return (
    <div className="bg-white rounded-xl border border-zinc-200 shadow-lg" data-testid="evaluate-detail-panel">
      {/* Header */}
      <div className="sticky top-0 bg-white/95 backdrop-blur border-b border-zinc-100 px-5 sm:px-6 py-4 rounded-t-xl flex items-start justify-between gap-3 z-10">
        <div className="flex items-start gap-3 min-w-0">
          {h.logo_url ? (
            <img src={h.logo_url} alt="" className="w-12 h-12 rounded bg-zinc-100 flex-shrink-0 object-contain" />
          ) : (
            <div className="w-12 h-12 rounded bg-zinc-100 flex-shrink-0 flex items-center justify-center text-base font-bold text-zinc-500">{h.name?.[0] || "?"}</div>
          )}
          <div className="min-w-0">
            <div className="flex items-center gap-2 flex-wrap mb-1">
              {h.source && <span className="text-[10px] font-medium px-1.5 py-0.5 rounded bg-violet-100 text-violet-700">{h.source === "ycombinator" ? "YC" : h.source}</span>}
              {h.acquired && <span className="text-[10px] font-medium px-1.5 py-0.5 rounded bg-emerald-100 text-emerald-700">✓ exited</span>}
              {h.best_gap_score > 0 && <GapBadge score={h.best_gap_score} />}
            </div>
            <h2 className="text-xl font-bold text-zinc-900 leading-tight" data-testid="eval-detail-title">{h.name}</h2>
            <div className="text-[12px] text-zinc-500 mt-0.5">
              {INDUSTRY_LABELS[h.industry_bucket] || h.industry_bucket} · {h.cluster_label}
            </div>
            <div className="text-[11px] text-zinc-500 mt-1 flex flex-wrap gap-x-2 gap-y-0.5">
              {h.founded_year && <span>founded {h.founded_year}</span>}
              {h.team_size && <span>· {h.team_size} people</span>}
              {h.country && <span>· {h.country}{h.city ? `, ${h.city}` : ""}</span>}
              {h.lead_founder_name && <span>· founder {h.lead_founder_name}</span>}
              {h.website && <span>· <a href={h.website} target="_blank" rel="noopener noreferrer" className="text-violet-700 hover:underline">{h.website.replace(/^https?:\/\//, "").replace(/\/$/, "")}</a></span>}
            </div>
          </div>
        </div>
        <button onClick={onClose} className="flex-shrink-0 text-zinc-500 hover:text-zinc-900 text-xl leading-none px-2 py-1 rounded hover:bg-zinc-100" data-testid="eval-detail-close">×</button>
      </div>

      <div className="p-5 sm:p-6 space-y-5">
        {/* SHOULD I BUILD THIS? — instant go/no-go verdict */}
        <BuildVerdict data={data} name={h.name} id={id} />

        {/* QUICK READ — plain-English summary of the whole teardown */}
        <QuickRead data={data} name={h.name} />

        {/* THE BIG IDEA (synthesis) — hero */}
        {syn?.available && (
          <div className="rounded-xl border border-violet-200 bg-gradient-to-br from-violet-50 to-white overflow-hidden" data-testid="synthesis-block">
            <div className="px-4 py-2.5 bg-violet-600 text-white text-xs font-semibold flex items-center gap-2">
              <span>💡 The big idea</span>
            </div>
            <div className="p-4">
              <div className="text-[11px] font-semibold uppercase tracking-wider text-violet-500 mb-1">Our take</div>
              <p className="text-[15px] sm:text-base leading-relaxed text-zinc-900 font-medium" data-testid="synthesis-headline">{syn.headline}</p>
              {primaryPlain && (
                <p className="text-[12px] text-zinc-500 mt-2">
                  Main angle: <span className="font-medium text-zinc-700">{primaryPlain}</span>
                  {backedBy.length > 0 && <> · backed by {backedBy.join(", ")}</>}
                </p>
              )}
              {syn.lens_evidence && Object.keys(syn.lens_evidence).length > 0 && (
                <div className="mt-2 pt-2 border-t border-violet-100 space-y-0.5" data-testid="synthesis-lens-evidence">
                  <div className="text-[11px] font-semibold text-zinc-400">What this is based on</div>
                  {Object.entries(syn.lens_evidence).map(([k, v]) => (
                    <div key={k} className="text-[12px] text-zinc-600"><span className="font-semibold text-violet-700">{LENS_PLAIN[k] || k}</span> — {v}</div>
                  ))}
                </div>
              )}
            </div>
          </div>
        )}

        {/* Revenue proof — real indie products making money on a similar problem */}
        <RevenueProofCard proof={data.revenue_proof} />

        {/* The four evidence angles, in plain language */}
        <LensCard icon="🌍" title="Where you could launch it" subtitle="Markets with no local version yet" code="A" testid="lens-card-a"
          available={data.lens_a_geographic?.available} deferral={data.lens_a_geographic?.reason}>
          <LensA data={data.lens_a_geographic} />
        </LensCard>

        <LensCard icon="😤" title="What customers hate today" subtitle="Real complaints about the tools people use now" code="C" testid="lens-card-c"
          available={data.lens_c_pain?.available} deferral={data.lens_c_pain?.reason}>
          <LensC data={data.lens_c_pain} />
        </LensCard>

        <LensCard icon="🧩" title="Features it's missing" subtitle="What similar companies offer that this one may not" code="B" testid="lens-card-b"
          available={data.lens_b_features?.available} deferral={data.lens_b_features?.reason}>
          <LensB data={data.lens_b_features} centroidName={h.name} />
        </LensCard>

        <LensCard icon="💰" title="Pricing gaps" subtitle="Price points no competitor is covering" code="D" testid="lens-card-d"
          available={data.lens_d_market?.available} deferral={data.lens_d_market?.reason}>
          <LensD data={data.lens_d_market} />
        </LensCard>
      </div>
    </div>
  );
}

export default function EvaluateBrowser() {
  const [items, setItems] = useState([]);
  const [total, setTotal] = useState(0);
  const [facets, setFacets] = useState(null);
  const [loading, setLoading] = useState(false);
  const [openId, setOpenId] = useState(() => {
    const m = (typeof window !== "undefined" ? window.location.pathname : "").match(/^\/evaluate\/([a-zA-Z0-9]+)/);
    return m ? m[1] : null;
  });
  const [q, setQ] = useState("");
  const [industry, setIndustry] = useState("");
  const [hasPain, setHasPain] = useState(false);
  const [hasFeatures, setHasFeatures] = useState(false);
  const [strongOnly, setStrongOnly] = useState(false);
  const [offset, setOffset] = useState(0);
  const [lastRefresh, setLastRefresh] = useState(null);
  const limit = 60;

  useEffect(() => {
    axios.get(`${API}/evaluate/companies/facets`).then(r => setFacets(r.data)).catch(() => {});
    axios.get(`${API}/corpus/last-refresh`).then(r => setLastRefresh(r.data)).catch(() => {});
  }, []);

  // Deep-linkable teardowns: /evaluate/{id} opens a specific company so a
  // shared "Should I build this?" verdict link lands the recipient right on it.
  const openCompany = (id) => {
    setOpenId(id);
    if (typeof window !== "undefined" && window.location.pathname !== `/evaluate/${id}`) {
      window.history.pushState({}, "", `/evaluate/${id}`);
    }
    window.scrollTo({ top: 0, behavior: "smooth" });
  };
  const closeCompany = () => {
    setOpenId(null);
    if (typeof window !== "undefined" && window.location.pathname.startsWith("/evaluate/")) {
      window.history.pushState({}, "", "/evaluate");
    }
  };
  useEffect(() => {
    const onPop = () => {
      const m = window.location.pathname.match(/^\/evaluate\/([a-zA-Z0-9]+)/);
      setOpenId(m ? m[1] : null);
    };
    window.addEventListener("popstate", onPop);
    return () => window.removeEventListener("popstate", onPop);
  }, []);

  const queryString = useMemo(() => {
    const p = new URLSearchParams();
    if (q.trim()) p.set("q", q.trim());
    if (industry) p.set("industry", industry);
    if (hasPain) p.set("has_pain", "true");
    if (hasFeatures) p.set("has_features", "true");
    p.set("strong_only", strongOnly ? "true" : "false");
    p.set("limit", String(limit));
    p.set("offset", String(offset));
    return p.toString();
  }, [q, industry, hasPain, hasFeatures, strongOnly, offset]);

  useEffect(() => {
    let cancel = false;
    setLoading(true);
    (async () => {
      try {
        const r = await axios.get(`${API}/evaluate/companies?${queryString}`);
        if (!cancel) { setItems(r.data.items || []); setTotal(r.data.total || 0); }
      } catch {
        if (!cancel) { setItems([]); setTotal(0); }
      } finally {
        if (!cancel) setLoading(false);
      }
    })();
    return () => { cancel = true; };
  }, [queryString]);

  return (
    <div className="max-w-7xl mx-auto py-4 sm:py-6 px-2 sm:px-0" data-testid="evaluate-browser-root">
      <div className="mb-5">
        <div className="text-[11px] font-semibold uppercase tracking-[0.2em] text-violet-700 mb-1">
          Evaluate
        </div>
        <h1 className="text-2xl sm:text-3xl font-bold text-zinc-900 leading-tight flex items-center gap-2">
          Pick a company. See how to beat it.
          <GuideHelpButton feature="evaluate" />
        </h1>
        <p className="text-sm text-zinc-600 mt-1 max-w-3xl leading-relaxed">
          Choose any VC-backed company and we'll show you, in plain English, where its idea is missing,
          what its rivals' customers hate, the features it lacks, and the pricing gaps you could exploit —
          each backed by a real source. When we don't have data, we say so instead of guessing.
        </p>
        <div className="mt-2 flex items-center gap-4 flex-wrap">
          <label className="flex items-center gap-2 text-[13px] text-zinc-700 cursor-pointer">
            <input type="checkbox" checked={strongOnly} onChange={e => { setStrongOnly(e.target.checked); setOffset(0); }} data-testid="evaluate-strong-only-toggle" />
            <span>Only strong opportunities</span>
          </label>
          <label className="flex items-center gap-2 text-[13px] text-zinc-700 cursor-pointer">
            <input type="checkbox" checked={hasFeatures} onChange={e => { setHasFeatures(e.target.checked); setOffset(0); }} data-testid="evaluate-has-features-toggle" />
            <span>Only companies with a feature breakdown</span>
          </label>
        </div>
      </div>

      {/* Coverage strip — plain language */}
      <div className="mb-4 rounded-lg border border-zinc-200 bg-zinc-50 px-3 py-2 text-[12px] text-zinc-600 leading-relaxed" data-testid="evaluate-mvp-banner">
        Covering <span className="font-semibold text-zinc-800">{facets?.distinct_companies?.toLocaleString() ?? "—"}</span> analysed companies.
        {facets && !facets.feature_extraction_complete && (
          <span> Feature breakdowns are still being added in the background — re-open a company later to see more.</span>
        )}
        {" "}Everything you see links to its source; nothing is made up.
      </div>
      {lastRefresh?.last_refreshed_at && (
        <div className="mb-4 -mt-2 inline-flex items-center gap-1.5 text-[11px] text-zinc-500" data-testid="evaluate-last-refresh">
          <span className="inline-block w-1.5 h-1.5 rounded-full bg-emerald-500" />
          Data last refreshed {new Date(lastRefresh.last_refreshed_at).toLocaleDateString(undefined, { year: "numeric", month: "short", day: "numeric" })}
          <span className="text-zinc-400">· refreshes every {lastRefresh.interval_days} days</span>
        </div>
      )}

      <div className="grid grid-cols-1 lg:grid-cols-12 gap-5">
        {/* Sidebar */}
        <aside className="lg:col-span-3 space-y-3" data-testid="evaluate-filters">
          <div className="bg-white rounded-lg border border-zinc-200 p-3">
            <input
              type="search"
              placeholder="Search company or category…"
              value={q}
              onChange={e => { setQ(e.target.value); setOffset(0); }}
              className="w-full text-sm px-3 py-2 rounded border border-zinc-200 focus:border-violet-500 focus:outline-none"
              data-testid="evaluate-search"
            />
            <div className="mt-3">
              <div className="text-[11px] font-semibold uppercase tracking-wider text-zinc-500 mb-1.5">Industry</div>
              <div className="flex flex-wrap gap-1.5">
                <button onClick={() => { setIndustry(""); setOffset(0); }}
                  className={`text-[11px] px-2 py-1 rounded-full border ${industry === "" ? "bg-violet-600 text-white border-violet-600" : "bg-white text-zinc-700 border-zinc-200"}`}>all</button>
                {(facets?.industries || []).map(i => (
                  <button key={i} onClick={() => { setIndustry(i); setOffset(0); }}
                    className={`text-[11px] px-2 py-1 rounded-full border ${industry === i ? "bg-violet-600 text-white border-violet-600" : "bg-white text-zinc-700 border-zinc-200"}`}
                    data-testid={`evaluate-filter-industry-${i}`}>
                    {INDUSTRY_LABELS[i] || i}
                  </button>
                ))}
              </div>
            </div>
            <label className="flex items-center gap-2 mt-3 text-[12px] text-zinc-700 cursor-pointer">
              <input type="checkbox" checked={hasPain} onChange={e => { setHasPain(e.target.checked); setOffset(0); }} data-testid="evaluate-has-pain-toggle" />
              Only show industries with customer-complaint data
            </label>
          </div>

          {/* How to read this — replaces the cryptic legend */}
          <div className="bg-white rounded-lg border border-zinc-200 p-3.5 text-[12px] text-zinc-600 leading-relaxed" data-testid="evaluate-howto">
            <div className="font-semibold text-zinc-800 mb-2">How to read a teardown</div>
            <ul className="space-y-2">
              <li>🌍 <b>Where to launch it</b> — markets with no local version yet.</li>
              <li>😤 <b>What customers hate today</b> — real complaints about today's tools.</li>
              <li>🧩 <b>Features it's missing</b> — what rivals offer that it may not.</li>
              <li>💰 <b>Pricing gaps</b> — price tiers no competitor covers.</li>
              <li>💡 <b>The big idea</b> — our one-line recommendation, citing the above.</li>
            </ul>
            <div className="mt-3 pt-3 border-t border-zinc-100 text-[11px] text-zinc-500">
              The opportunity score: <b>under 2</b> small · <b>2–3</b> worth a look · <b>3–5</b> strong · <b>5+</b> rare. Higher = fewer competitors.
            </div>
          </div>
        </aside>

        <main className="lg:col-span-9">
          <div className="flex items-center justify-between mb-3 px-1">
            <div className="text-xs text-zinc-600" data-testid="evaluate-count">
              {loading ? "Finding companies…" : `${total.toLocaleString()} companies`}
            </div>
          </div>

          {openId && (
            <div className="mb-5">
              <EvaluateDetailPanel id={openId} onClose={closeCompany} />
            </div>
          )}

          <div className="space-y-2" data-testid="evaluate-list">
            {loading && items.length === 0 && (
              Array.from({ length: 6 }).map((_, i) => (
                <div key={`esk-${i}`} className="bg-white rounded-lg border border-zinc-200 px-4 py-3 animate-pulse" data-testid="evaluate-skeleton-row">
                  <div className="flex items-start gap-3">
                    <div className="w-10 h-10 rounded bg-zinc-200 flex-shrink-0" />
                    <div className="min-w-0 flex-1 space-y-2">
                      <div className="h-4 w-1/2 rounded bg-zinc-200" />
                      <div className="h-3 w-1/3 rounded bg-zinc-100" />
                      <div className="h-3 w-1/4 rounded bg-zinc-100" />
                    </div>
                    <div className="h-6 w-24 rounded-full bg-zinc-200 flex-shrink-0" />
                  </div>
                </div>
              ))
            )}
            {items.map(c => (
              <button key={c.id}
                onClick={() => openCompany(c.id)}
                className={`w-full text-left bg-white hover:bg-violet-50 transition-colors rounded-lg border border-zinc-200 hover:border-violet-300 px-4 py-3 ${openId === c.id ? "ring-2 ring-violet-400" : ""}`}
                data-testid={`evaluate-row-${c.id}`}>
                <div className="flex items-start gap-3">
                  {c.logo_url ? (
                    <img src={c.logo_url} alt="" className="w-10 h-10 rounded bg-zinc-100 flex-shrink-0 object-contain" loading="lazy" />
                  ) : (
                    <div className="w-10 h-10 rounded bg-zinc-100 flex-shrink-0 flex items-center justify-center text-[10px] font-bold text-zinc-500">{c.name?.[0] || "?"}</div>
                  )}
                  <div className="min-w-0 flex-1">
                    <div className="flex items-center gap-1.5 flex-wrap mb-0.5">
                      {c.source && <span className="text-[9px] font-medium uppercase px-1.5 py-0.5 rounded bg-violet-100 text-violet-700">{c.source === "ycombinator" ? "YC" : c.source}</span>}
                      {c.acquired && <span className="text-[9px] font-medium uppercase px-1.5 py-0.5 rounded bg-emerald-100 text-emerald-700">✓ exit</span>}
                      {c.has_features && <span className="text-[9px] font-medium px-1.5 py-0.5 rounded bg-blue-50 text-blue-700 border border-blue-200" title="Feature breakdown available">🧩 features</span>}
                    </div>
                    <h3 className="font-semibold text-zinc-900 text-[15px] truncate">{c.name}</h3>
                    <div className="text-[11px] text-zinc-500 mt-0.5 truncate">
                      {INDUSTRY_LABELS[c.industry_bucket] || c.industry_bucket || "uncategorized"} · {c.cluster_label || "no category"}
                    </div>
                    <div className="text-[11px] text-zinc-500 mt-1 flex flex-wrap gap-x-2">
                      {c.founded_year && <span>founded {c.founded_year}</span>}
                      {c.team_size && <span>· {c.team_size} people</span>}
                      {c.country && <span>· {c.country}</span>}
                    </div>
                  </div>
                  <div className="text-right flex-shrink-0 flex items-center">
                    {c.best_gap_score > 0
                      ? <GapBadge score={c.best_gap_score} />
                      : <span className="text-[10px] text-zinc-400">no opportunity score yet</span>}
                  </div>
                </div>
              </button>
            ))}
            {!loading && items.length === 0 && (
              <div className="text-center py-10 text-zinc-500 text-sm" data-testid="evaluate-empty">No companies match these filters.</div>
            )}
          </div>

          {total > limit && (
            <div className="flex items-center justify-between mt-4 px-1">
              <button onClick={() => { setOffset(Math.max(0, offset - limit)); window.scrollTo({ top: 0, behavior: "smooth" }); }} disabled={offset === 0}
                data-testid="evaluate-pagination-prev"
                className="text-xs px-4 py-2.5 rounded border border-zinc-200 bg-white hover:bg-zinc-50 disabled:opacity-40">‹ prev</button>
              <span className="text-[11px] text-zinc-500" data-testid="evaluate-pagination-info">{offset + 1}–{Math.min(offset + limit, total)} of {total}</span>
              <button onClick={() => { setOffset(offset + limit); window.scrollTo({ top: 0, behavior: "smooth" }); }} disabled={offset + limit >= total}
                data-testid="evaluate-pagination-next"
                className="text-xs px-4 py-2.5 rounded border border-zinc-200 bg-white hover:bg-zinc-50 disabled:opacity-40">next ›</button>
            </div>
          )}
        </main>
      </div>
    </div>
  );
}
